import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase,
  Calendar,
  User,
  Users,
  MapPin,
  Info,
  Building2,
  Clock,
  Target,
  Settings,
  Phone,
  Mail,
  Wrench,
  UserCheck,
  Hash,
  DollarSign,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Activity,
  ArrowLeft,
  Edit,
  Loader2,
  Package,
  UserCog,
  FileText,
  Download,
  Cpu,
  Zap,
  Compass,
  ShieldCheck,
  History,
  ClipboardList,
  Globe,
  Layers,
  ExternalLink,
  Image as ImageIcon,
} from "lucide-react";
import PageBreadcrumb from "../../../../shared/PageBreadCrumb";
import RightDrawer from "../../../../shared/components/form/RightDrawer";
import ProjectAssetManagement from "../../components/ProjectAssetManagement";
import ProjectProgressHeader from "../../components/ProjectProgressHeader";
import ProjectProgressTab from "../../components/progress/ProjectProgressTab";
import { isProjectInfoComplete } from "../../utils/projectStatusUtils";
import { safeFormatDate, safeFormatDistance } from "../../../../utils/dateUtils";
import projectService from "../../../../services/projectService";
import { useResolvedMediaUrl } from "../../../../hooks/useResolvedMediaUrl";
import { toast } from "react-toastify";

const PROJECT_TABS = [
  { key: "overview", label: "Overview", icon: Info },
  { key: "progress", label: "Progress", icon: Activity },
] as const;
type ProjectTab = typeof PROJECT_TABS[number]["key"];

function getStatusConfig(status: string) {
  const normalizedStatus = status?.toLowerCase() || "unknown";
  switch (normalizedStatus) {
    case "active":
    case "operational":
      return {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        border: "border-emerald-200",
        dot: "bg-emerald-500",
        icon: CheckCircle2,
      };
    case "completed":
      return {
        bg: "bg-blue-50",
        text: "text-blue-700",
        border: "border-blue-200",
        dot: "bg-blue-500",
        icon: CheckCircle2,
      };
    case "pending":
    case "planning":
      return {
        bg: "bg-amber-50",
        text: "text-amber-700",
        border: "border-amber-200",
        dot: "bg-amber-500",
        icon: Clock,
      };
    case "on-hold":
      return {
        bg: "bg-orange-50",
        text: "text-orange-700",
        border: "border-orange-200",
        dot: "bg-orange-500",
        icon: AlertCircle,
      };
    case "cancelled":
      return {
        bg: "bg-red-50",
        text: "text-red-700",
        border: "border-red-200",
        dot: "bg-red-500",
        icon: XCircle,
      };
    default:
      return {
        bg: "bg-slate-50",
        text: "text-slate-700",
        border: "border-slate-200",
        dot: "bg-slate-400",
        icon: Activity,
      };
  }
}

function getApprovalConfig(status: string) {
  const s = (status || "").toLowerCase();
  switch (s) {
    case "approved":
      return { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", label: "Approved" };
    case "pending":
    case "submitted":
      return { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", label: "Pending Approval" };
    case "rejected":
      return { bg: "bg-red-50", text: "text-red-700", border: "border-red-200", label: "Rejected" };
    case "draft":
      return { bg: "bg-slate-50", text: "text-slate-600", border: "border-slate-200", label: "Draft" };
    default:
      return null;
  }
}

function getLocationString(project: any): string {
  if (
    project.locationId &&
    typeof project.locationId === "object" &&
    project.locationId.name
  ) {
    return project.locationId.name;
  }
  if (project.location && project.location !== "undefined") {
    return project.location;
  }
  if (project.city && project.city !== "undefined") {
    return project.city;
  }
  return "Not specified";
}

function getInitials(name: string): string {
  if (!name) return "P";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join("");
}

/* Compact info row - industry standard key-value pair */
function InfoRow({
  icon: Icon,
  label,
  value,
  className = "",
  valueClassName = "",
  subValue,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number | null | undefined;
  className?: string;
  valueClassName?: string;
  subValue?: string;
}) {
  if (value == null || value === "" || value === "undefined") return null;
  return (
    <div
      className={`flex ${subValue ? 'items-start' : 'items-center'} gap-2 py-1.5 px-2 rounded border-b border-slate-50 last:border-b-0 ${className}`}
    >
      <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[11px] text-slate-500 flex-shrink-0">{label}</span>
          <span
            className={`text-xs font-medium text-slate-800 text-right break-words ${valueClassName}`}
          >
            {String(value)}
          </span>
        </div>
        {subValue && (
          <span className="text-[10px] text-slate-400 text-right block">
            {subValue}
          </span>
        )}
      </div>
    </div>
  );
}

/* Compact section card - industry standard dense layout */
function SectionCard({
  title,
  icon: Icon,
  children,
  delay = 0,
  accent = "teal",
  action,
  className = "",
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  delay?: number;
  accent?: "teal" | "violet" | "blue" | "emerald" | "amber" | "rose" | "slate";
  action?: React.ReactNode;
  className?: string;
}) {
  const accentMap: Record<string, string> = {
    teal: "text-teal-600",
    violet: "text-violet-600",
    blue: "text-blue-600",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    rose: "text-rose-600",
    slate: "text-slate-600",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.2 }}
      className={`bg-white rounded-lg border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors ${className}`}
    >
      <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/80 flex items-center gap-1.5">
        <Icon className={`w-3.5 h-3.5 ${accentMap[accent]}`} />
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex-1">
          {title}
        </h3>
        {action}
      </div>
      <div className="p-1.5">{children}</div>
    </motion.div>
  );
}

/* Empty placeholder when a section has no data */
function EmptyHint({ text }: { text: string }) {
  return (
    <div className="px-2 py-3 text-[11px] text-slate-400 italic text-center">
      {text}
    </div>
  );
}

/* Document thumbnail / preview link with signed-URL resolution */
function DocumentItem({ doc }: { doc: any }) {
  const url = useResolvedMediaUrl(doc.documentFile);
  const isImage = /\.(jpe?g|png|gif|webp|svg)(\?|$)/i.test(doc.documentFile || "");
  const ext = (doc.documentFile || "").split(".").pop()?.split("?")[0]?.toUpperCase() || "FILE";

  return (
    <div className="group flex items-center gap-2.5 p-2 rounded-md border border-slate-200 hover:border-teal-300 hover:bg-teal-50/30 transition-all">
      <div className="w-10 h-10 rounded-md bg-slate-100 border border-slate-200 flex-shrink-0 overflow-hidden flex items-center justify-center">
        {isImage && url ? (
          <img src={url} alt={doc.documentName} className="w-full h-full object-cover" />
        ) : isImage ? (
          <ImageIcon className="w-4 h-4 text-slate-400" />
        ) : (
          <div className="flex flex-col items-center justify-center">
            <FileText className="w-4 h-4 text-teal-600" />
            <span className="text-[7px] font-bold text-slate-500 mt-0.5">{ext}</span>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-semibold text-slate-800 truncate">
          {doc.documentName || "Untitled document"}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
            {doc.documentType || "Document"}
          </span>
          {doc.uploadedAt && (
            <span>{safeFormatDate(doc.uploadedAt, "MMM dd, yyyy")}</span>
          )}
        </div>
      </div>
      {url && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded hover:bg-white border border-transparent hover:border-slate-200 text-slate-500 hover:text-teal-600"
            title="Open"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <a
            href={url}
            download
            className="p-1.5 rounded hover:bg-white border border-transparent hover:border-slate-200 text-slate-500 hover:text-teal-600"
            title="Download"
          >
            <Download className="w-3.5 h-3.5" />
          </a>
        </div>
      )}
    </div>
  );
}

export default function ViewProject() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [openEquipmentDrawer, setOpenEquipmentDrawer] = useState(false);
  const [progressRefreshKey, setProgressRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState<ProjectTab>("overview");

  useEffect(() => {
    if (projectId) {
      loadProject();
    }
  }, [projectId]);

  const loadProject = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const data = await projectService.getProjectById(projectId);
      setProject(data);
    } catch (error: unknown) {
      const err = error as { message?: string };
      toast.error(err?.message || "Failed to load project");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => navigate("/projects");
  const handleEdit = () =>
    navigate(`/projects/edit/${project?._id || project?.id}`);
  const handleOpenEquipment = () => {
    if (!isProjectInfoComplete(project)) {
      navigate(`/projects/edit/${project?._id || project?.id}?completeSetup=1`);
      return;
    }
    setOpenEquipmentDrawer(true);
  };
  const handleAssignments = () =>
    navigate(`/projects/${project?._id || project?.id}/assignments`);
  const handleManageResources = () =>
    navigate(`/projects/${project?._id || project?.id}/manage-resources`);
  const handleCalendar = () =>
    navigate(`/projects/${project?._id || project?.id}/calendar`);

  /* Aggregated counts for the team strip */
  const teamCounts = useMemo(() => {
    if (!project?.assignedResources) return { eng: 0, sup: 0 };
    return {
      eng: project.assignedResources.siteEngineers?.length || 0,
      sup: project.assignedResources.siteSupervisors?.length || 0,
    };
  }, [project]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-teal-600" />
          <p className="text-slate-600 font-medium">Loading project…</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center bg-white rounded-xl border border-slate-200 p-12">
          <Briefcase className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Project not found</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(
    project.projectStatus || project.status
  );
  const StatusIcon = statusConfig.icon;
  const approvalConfig = getApprovalConfig(project.approvalStatus);
  const locationDisplay = getLocationString(project);
  const projectName = project.projectName || project.name || "Untitled Project";
  const projectCode = project.projectId || project.code || "N/A";
  const documents: any[] = Array.isArray(project.attachedDocuments)
    ? project.attachedDocuments
    : [];

  const customer =
    (typeof project.customerId === "object" ? project.customerId : null) ||
    project.customer ||
    null;
  const locationObj =
    typeof project.locationId === "object" ? project.locationId : null;

  /* Site coordinates: prefer project.* then nested locationId.* */
  const lat = project.latitude ?? locationObj?.latitude;
  const lng = project.longitude ?? locationObj?.longitude;
  const mapsUrl =
    lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : null;

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-8">
      <PageBreadcrumb
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Projects", path: "/projects" },
          {
            name: projectName,
            path: undefined,
          },
        ]}
      />

      {/* === HERO HEADER === */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="relative overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-white via-teal-50/40 to-violet-50/30 mb-4"
      >
        {/* decorative blob */}
        <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-teal-200/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-10 w-60 h-60 rounded-full bg-violet-200/30 blur-3xl pointer-events-none" />

        <div className="relative px-4 sm:px-5 py-4">
          {/* Top row: back + breadcrumb-ish */}
          <div className="flex items-center justify-between gap-2 mb-3">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-white/70 rounded-md transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Projects
            </button>
            <div className="flex items-center gap-1.5">
              {approvalConfig && (
                <div
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-semibold uppercase tracking-wide ${approvalConfig.bg} ${approvalConfig.border} ${approvalConfig.text}`}
                >
                  <ShieldCheck className="w-3 h-3" />
                  {approvalConfig.label}
                </div>
              )}
              <div
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-semibold ${statusConfig.bg} ${statusConfig.border} ${statusConfig.text}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot} animate-pulse`} />
                <StatusIcon className="w-3.5 h-3.5" />
                {project.projectStatus || project.status || "Unknown"}
              </div>
            </div>
          </div>

          {/* Title row with avatar */}
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white text-lg sm:text-xl font-bold shadow-md">
                {getInitials(projectName)}
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border-2 border-white shadow-sm flex items-center justify-center">
                <Briefcase className="w-2.5 h-2.5 text-teal-600" />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-2xl font-bold text-slate-900 leading-tight truncate">
                    {projectName}
                  </h1>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] font-mono text-slate-700">
                      <Hash className="w-3 h-3 text-slate-400" />
                      {projectCode}
                    </span>
                    {(project.projectTypeId?.name || project.projectType) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] text-slate-700">
                        <Layers className="w-3 h-3 text-violet-500" />
                        {project.projectTypeId?.name || project.projectType}
                      </span>
                    )}
                    {project.projectCategory && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] text-slate-700">
                        <Target className="w-3 h-3 text-blue-500" />
                        {project.projectCategory}
                      </span>
                    )}
                    {project.priority && (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold ${
                          project.priority === "High"
                            ? "bg-red-50 text-red-700 border-red-200"
                            : project.priority === "Medium"
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"
                        }`}
                      >
                        <AlertCircle className="w-3 h-3" />
                        {project.priority}
                      </span>
                    )}
                    {locationDisplay !== "Not specified" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] text-slate-700">
                        <MapPin className="w-3 h-3 text-rose-500" />
                        {locationDisplay}
                      </span>
                    )}
                  </div>
                  {project.description && (
                    <p className="mt-2 text-xs sm:text-sm text-slate-600 leading-relaxed line-clamp-2 max-w-3xl">
                      {project.description}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-md transition-colors shadow-sm flex-shrink-0"
                >
                  <Edit className="w-3.5 h-3.5" />
                  Edit Project
                </button>
              </div>
            </div>
          </div>

          {/* === KPI strip === */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {/* Progress */}
            <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200 px-3 py-2">
              <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                Progress
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-teal-500 to-teal-600 h-full rounded-full transition-all"
                    style={{ width: `${project.progress || 0}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-teal-700">
                  {project.progress || 0}%
                </span>
              </div>
            </div>
            {/* Start */}
            <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200 px-3 py-2">
              <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                Start
              </div>
              <div className="text-xs font-semibold text-slate-800">
                {project.startDate
                  ? safeFormatDate(project.startDate, "MMM dd, yyyy")
                  : "—"}
              </div>
            </div>
            {/* End */}
            <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200 px-3 py-2">
              <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                End
              </div>
              <div className="text-xs font-semibold text-slate-800">
                {project.endDate
                  ? safeFormatDate(project.endDate, "MMM dd, yyyy")
                  : "—"}
              </div>
            </div>
            {/* DC Capacity */}
            <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200 px-3 py-2">
              <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Zap className="w-2.5 h-2.5" /> DC Capacity
              </div>
              <div className="text-xs font-semibold text-slate-800">
                {project.DCCapacity || "—"}
              </div>
            </div>
            {/* AC Capacity */}
            <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200 px-3 py-2">
              <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1">
                <Cpu className="w-2.5 h-2.5" /> AC Capacity
              </div>
              <div className="text-xs font-semibold text-slate-800">
                {project.ACCapacity || "—"}
              </div>
            </div>
            {/* Team */}
            <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200 px-3 py-2">
              <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                Team Size
              </div>
              <div className="text-xs font-semibold text-slate-800">
                {1 +
                  (project.assignedResources?.projectHead ? 1 : 0) +
                  teamCounts.eng +
                  teamCounts.sup}{" "}
                <span className="text-[10px] text-slate-500 font-normal">
                  ({teamCounts.eng}E · {teamCounts.sup}S)
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* === Quick action toolbar === */}
      <div className="flex flex-wrap items-center gap-2 mb-4 p-2.5 bg-white rounded-lg border border-slate-200 shadow-sm">
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-1">
          Quick Actions
        </span>
        <button
          onClick={handleOpenEquipment}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-teal-50 hover:text-teal-700 border border-slate-200 hover:border-teal-300 rounded-md transition-colors"
        >
          <Package className="w-3.5 h-3.5" />
          Equipment & Assets
        </button>
        <button
          onClick={handleAssignments}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-blue-50 hover:text-blue-700 border border-slate-200 hover:border-blue-300 rounded-md transition-colors"
        >
          <Users className="w-3.5 h-3.5" />
          Assignments
        </button>
        <button
          onClick={handleManageResources}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-violet-50 hover:text-violet-700 border border-slate-200 hover:border-violet-300 rounded-md transition-colors"
        >
          <UserCog className="w-3.5 h-3.5" />
          Resources
        </button>
        <button
          onClick={handleCalendar}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-amber-50 hover:text-amber-700 border border-slate-200 hover:border-amber-300 rounded-md transition-colors"
        >
          <Calendar className="w-3.5 h-3.5" />
          Calendar
        </button>
      </div>

      {/* Tab strip */}
      <div className="mb-4 flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm w-fit">
        {PROJECT_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                active
                  ? "bg-teal-600 text-white shadow"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "progress" && projectId && (
        <ProjectProgressTab projectId={projectId} />
      )}

      {activeTab === "overview" && (
        <>
      {/* Key metrics bar - compact horizontal strip */}
      <div className="flex flex-wrap gap-2 mb-4">
        {typeof project.progress === "number" && (
          <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-3 py-2 min-w-[120px]">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
                Progress
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="bg-teal-600 h-full rounded-full"
                    style={{ width: `${project.progress}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-teal-700 flex-shrink-0">
                  {project.progress}%
                </span>
              </div>
            </div>
          </div>
        )}
        {project.startDate && (
          <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 min-w-[100px]">
            <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
              Start
            </div>
            <div className="text-xs font-medium text-slate-800">
              {safeFormatDate(project.startDate, "MMM dd, yyyy")}
            </div>
          </div>
        )}
        {project.endDate && (
          <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 min-w-[100px]">
            <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
              End
            </div>
            <div className="text-xs font-medium text-slate-800">
              {safeFormatDate(project.endDate, "MMM dd, yyyy")}
            </div>
          </div>
        )}
        {project.budgetAmount && (
          <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 min-w-[100px]">
            <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
              Budget
            </div>
            <div className="text-xs font-medium text-emerald-700">
              ₹{Number(project.budgetAmount).toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {/* Content grid - 3 columns on xl for denser layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {/* PROJECT INFORMATION */}
        <SectionCard title="Project Information" icon={Info} delay={0.05} accent="teal">
          <div className="space-y-0">
            <InfoRow icon={Hash} label="Project ID" value={projectCode} valueClassName="font-mono" />
            <InfoRow icon={Briefcase} label="Project Name" value={projectName} valueClassName="font-semibold" />
            <InfoRow
              icon={Layers}
              label="Type"
              value={
                project.projectTypeId?.name ||
                project.projectType ||
                project.category
              }
            />
            <InfoRow
              icon={Target}
              label="Category"
              value={project.projectCategory}
            />
            <InfoRow
              icon={Settings}
              label="Priority"
              value={project.priority}
              valueClassName={
                project.priority === "High"
                  ? "text-red-600 font-semibold"
                  : project.priority === "Medium"
                    ? "text-amber-600 font-semibold"
                    : "text-emerald-600 font-semibold"
              }
            />
            {project.description && (
              <div className="p-2 mt-1 bg-slate-50 rounded border border-slate-100">
                <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-0.5">
                  Description
                </div>
                <p className="text-xs text-slate-700 leading-snug">
                  {project.description}
                </p>
              </div>
            )}
          </div>
        </SectionCard>

        {/* CAPACITY & SPECS */}
        <SectionCard title="Capacity & Specifications" icon={Zap} delay={0.06} accent="amber">
          <div className="space-y-0">
            <InfoRow icon={Zap} label="DC Capacity" value={project.DCCapacity} valueClassName="font-mono text-amber-700" />
            <InfoRow icon={Cpu} label="AC Capacity" value={project.ACCapacity} valueClassName="font-mono text-amber-700" />
            <InfoRow
              icon={Activity}
              label="Installed Capacity"
              value={
                project.capacity?.installedCapacity
                  ? `${project.capacity.installedCapacity} ${project.capacity.unit || ""}`
                  : null
              }
            />
            <InfoRow
              icon={Activity}
              label="Planned Capacity"
              value={
                project.capacity?.plannedCapacity
                  ? `${project.capacity.plannedCapacity} ${project.capacity.unit || ""}`
                  : null
              }
            />
            {!project.DCCapacity &&
              !project.ACCapacity &&
              !project.capacity?.installedCapacity &&
              !project.capacity?.plannedCapacity && (
                <EmptyHint text="No capacity data captured" />
              )}
          </div>
        </SectionCard>

        {/* CUSTOMER */}
        <SectionCard title="Customer" icon={Building2} delay={0.08} accent="blue">
          <div className="space-y-0">
            {customer?.companyName ? (
              <>
                <div className="px-2 pt-1 pb-2 mb-1 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs font-bold shadow-sm flex-shrink-0">
                      {getInitials(customer.companyName)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-slate-900 truncate">
                        {customer.companyName}
                      </div>
                      {customer.industry && (
                        <div className="text-[10px] text-slate-500">
                          {customer.industry}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <InfoRow icon={User} label="Contact" value={customer.name} />
                <InfoRow
                  icon={Mail}
                  label="Email"
                  value={customer.email}
                  valueClassName="text-blue-600 hover:underline cursor-pointer break-all"
                />
                <InfoRow icon={Phone} label="Phone" value={customer.phone} />
                <InfoRow icon={Globe} label="Website" value={customer.website} valueClassName="text-blue-600" />
                <InfoRow icon={Hash} label="GSTIN" value={customer.gstin} valueClassName="font-mono" />
              </>
            ) : (
              <EmptyHint text="No customer linked" />
            )}
          </div>
        </SectionCard>

        {/* LOCATION */}
        <SectionCard
          title="Location Details"
          icon={MapPin}
          delay={0.1}
          accent="rose"
          action={
            mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-rose-600 hover:text-rose-700"
              >
                <ExternalLink className="w-3 h-3" />
                Map
              </a>
            )
          }
        >
          <div className="space-y-0">
            <InfoRow icon={MapPin} label="Site" value={locationDisplay} valueClassName="font-semibold" />
            <InfoRow icon={MapPin} label="Address" value={project.address || locationObj?.address} />
            <InfoRow icon={MapPin} label="City" value={project.city || locationObj?.city} />
            <InfoRow icon={MapPin} label="State" value={project.state || locationObj?.state} />
            <InfoRow icon={MapPin} label="Country" value={project.country || locationObj?.country} />
            <InfoRow icon={Hash} label="Pincode" value={project.pincode || locationObj?.pincode} />
            {(lat || lng) && (
              <InfoRow
                icon={Compass}
                label="Coordinates"
                value={`${lat ?? "-"}, ${lng ?? "-"}`}
                valueClassName="font-mono text-[11px]"
              />
            )}
          </div>
        </SectionCard>

        {/* TEAM (spans 1 col on md, full row visually) */}
        <SectionCard title="Project Team" icon={Users} delay={0.12} accent="violet">
          <div className="space-y-1 p-1">
            {/* Project Manager */}
            {(project.assignedResources?.projectManager?.name ||
              project.manager?.name ||
              project.projectManager ||
              project.manager) && (
              <div className="flex items-center gap-2.5 px-2 py-2 rounded-md bg-teal-50/60 border border-teal-100">
                <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm">
                  {getInitials(
                    project.assignedResources?.projectManager?.name ||
                      project.manager?.name ||
                      (typeof project.manager === "string" ? project.manager : "M")
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-semibold text-slate-800 truncate">
                      {project.assignedResources?.projectManager?.name ||
                        project.manager?.name ||
                        project.projectManager ||
                        project.manager}
                    </span>
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-teal-100 text-teal-700 border border-teal-200 uppercase tracking-wide flex-shrink-0">
                      <User className="w-2.5 h-2.5" />
                      Manager
                    </span>
                  </div>
                  {project.assignedResources?.projectManager?.email && (
                    <p className="text-[10px] text-slate-500 truncate mt-0.5">
                      {project.assignedResources.projectManager.email}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Project Head */}
            {(project.assignedResources?.projectHead?.name ||
              project.projectHead ||
              project.projectHeadName) && (
              <div className="flex items-center gap-2.5 px-2 py-2 rounded-md bg-violet-50/60 border border-violet-100">
                <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm">
                  {getInitials(
                    project.assignedResources?.projectHead?.name ||
                      project.projectHead ||
                      project.projectHeadName ||
                      "H"
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-xs font-semibold text-slate-800 truncate">
                      {project.assignedResources?.projectHead?.name ||
                        project.projectHead ||
                        project.projectHeadName}
                    </span>
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-violet-100 text-violet-700 border border-violet-200 uppercase tracking-wide flex-shrink-0">
                      <UserCheck className="w-2.5 h-2.5" />
                      Head
                    </span>
                  </div>
                  {project.assignedResources?.projectHead?.email && (
                    <p className="text-[10px] text-slate-500 truncate mt-0.5">
                      {project.assignedResources.projectHead.email}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Site Supervisors */}
            {project.assignedResources?.siteSupervisors &&
              project.assignedResources.siteSupervisors.length > 0 && (
                <div className="pt-1.5 mt-0.5 border-t border-slate-100">
                  <div className="flex items-center justify-between gap-1.5 mb-1.5 px-1">
                    <div className="flex items-center gap-1.5">
                      <Wrench className="w-3 h-3 text-orange-500 flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                        Site Supervisors
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded-full border border-orange-200">
                      {project.assignedResources.siteSupervisors.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 px-1">
                    {project.assignedResources.siteSupervisors.map(
                      (supervisor: { name?: string; email?: string }, index: number) => (
                        <span
                          key={index}
                          title={supervisor.email}
                          className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-800 px-2 py-1 rounded-md text-[11px] font-medium border border-orange-200 shadow-sm"
                        >
                          <span className="w-4 h-4 rounded-full bg-orange-500 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">
                            {supervisor.name?.charAt(0)?.toUpperCase() || "S"}
                          </span>
                          {supervisor.name}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}

            {/* Site Engineers */}
            {project.assignedResources?.siteEngineers &&
              project.assignedResources.siteEngineers.length > 0 && (
                <div className="pt-1.5 mt-0.5 border-t border-slate-100">
                  <div className="flex items-center justify-between gap-1.5 mb-1.5 px-1">
                    <div className="flex items-center gap-1.5">
                      <UserCheck className="w-3 h-3 text-blue-500 flex-shrink-0" />
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                        Site Engineers
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-200">
                      {project.assignedResources.siteEngineers.length}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 px-1">
                    {project.assignedResources.siteEngineers.map(
                      (engineer: { name?: string; email?: string }, index: number) => (
                        <span
                          key={index}
                          title={engineer.email}
                          className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-800 px-2 py-1 rounded-md text-[11px] font-medium border border-blue-200 shadow-sm"
                        >
                          <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0">
                            {engineer.name?.charAt(0)?.toUpperCase() || "E"}
                          </span>
                          {engineer.name}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}

            {!project.assignedResources?.projectManager &&
              !project.manager &&
              !project.projectManager &&
              !project.assignedResources?.projectHead &&
              !project.projectHead &&
              (!project.assignedResources?.siteEngineers ||
                project.assignedResources.siteEngineers.length === 0) &&
              (!project.assignedResources?.siteSupervisors ||
                project.assignedResources.siteSupervisors.length === 0) && (
                <EmptyHint text="No team members assigned" />
              )}
          </div>
        </SectionCard>

        {/* TIMELINE */}
        <SectionCard title="Timeline" icon={Calendar} delay={0.14} accent="emerald">
          <div className="space-y-0">
            <InfoRow
              icon={Calendar}
              label="Start Date"
              value={
                project.startDate
                  ? safeFormatDate(project.startDate, "MMM dd, yyyy")
                  : null
              }
              subValue={
                project.startDate
                  ? safeFormatDistance(project.startDate, { addSuffix: true })
                  : undefined
              }
            />
            <InfoRow
              icon={Calendar}
              label="End Date"
              value={
                project.endDate
                  ? safeFormatDate(project.endDate, "MMM dd, yyyy")
                  : null
              }
              subValue={
                project.endDate
                  ? safeFormatDistance(project.endDate, { addSuffix: true })
                  : undefined
              }
            />
            <InfoRow
              icon={Clock}
              label="Duration"
              value={
                project.projectDurationMonths
                  ? `${project.projectDurationMonths} months`
                  : null
              }
            />
            <InfoRow
              icon={Target}
              label="Est. Completion"
              value={
                project.estimatedCompletionDate
                  ? safeFormatDate(
                      project.estimatedCompletionDate,
                      "MMM dd, yyyy"
                    )
                  : null
              }
            />
            <InfoRow
              icon={CheckCircle2}
              label="Actual Completion"
              value={
                project.actualCompletionDate
                  ? safeFormatDate(
                      project.actualCompletionDate,
                      "MMM dd, yyyy"
                    )
                  : null
              }
              valueClassName="text-emerald-600 font-semibold"
            />
            {!project.startDate && !project.endDate && (
              <EmptyHint text="No timeline set" />
            )}
          </div>
        </SectionCard>

        {/* FINANCIAL */}
        <SectionCard title="Financial" icon={DollarSign} delay={0.16} accent="emerald">
          <div className="space-y-0">
            <InfoRow
              icon={DollarSign}
              label="Budget"
              value={
                project.budgetAmount
                  ? `₹${Number(project.budgetAmount).toLocaleString()}`
                  : null
              }
              valueClassName="font-mono font-semibold text-emerald-700"
            />
            <InfoRow
              icon={DollarSign}
              label="Actual Cost"
              value={
                project.actualCost
                  ? `₹${Number(project.actualCost).toLocaleString()}`
                  : null
              }
              valueClassName="font-mono font-semibold text-blue-700"
            />
            {project.budgetAmount && project.actualCost && (
              <div className="px-2 pt-2 mt-1 border-t border-slate-100">
                <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Budget Utilization
                </div>
                {(() => {
                  const pct = Math.min(
                    100,
                    Math.round(
                      (Number(project.actualCost) /
                        Number(project.budgetAmount)) *
                        100
                    )
                  );
                  const over = Number(project.actualCost) > Number(project.budgetAmount);
                  return (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${over ? "bg-red-500" : "bg-emerald-500"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className={`text-xs font-bold ${over ? "text-red-700" : "text-emerald-700"}`}>
                        {pct}%
                      </span>
                    </div>
                  );
                })()}
              </div>
            )}
            {!project.budgetAmount && !project.actualCost && (
              <EmptyHint text="No financial data captured" />
            )}
          </div>
        </SectionCard>

        {/* DOCUMENTS — full width */}
        <SectionCard
          title={`Attached Documents${documents.length ? ` (${documents.length})` : ""}`}
          icon={FileText}
          delay={0.18}
          accent="blue"
          className="md:col-span-2 xl:col-span-3"
          action={
            <button
              onClick={handleEdit}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 hover:text-blue-700"
              title="Manage documents"
            >
              <Edit className="w-3 h-3" />
              Manage
            </button>
          }
        >
          {documents.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-xs text-slate-500 font-medium">No documents attached</p>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Upload manuals, certificates, specifications, etc.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-1">
              {documents.map((doc, idx) => (
                <DocumentItem key={idx} doc={doc} />
              ))}
            </div>
          )}
        </SectionCard>

        {/* AUDIT / META */}
        <SectionCard title="System & Audit" icon={History} delay={0.2} accent="slate">
          <div className="space-y-0">
            <InfoRow
              icon={Clock}
              label="Created"
              value={
                project.dateCreated || project.createdAt
                  ? safeFormatDate(
                      project.dateCreated || project.createdAt,
                      "MMM dd, yyyy"
                    )
                  : null
              }
              subValue={
                project.createdBy && typeof project.createdBy === "object"
                  ? `by ${project.createdBy.name || ""}`
                  : undefined
              }
            />
            <InfoRow
              icon={Clock}
              label="Last Modified"
              value={
                project.lastmodifiedDate || project.updatedAt
                  ? safeFormatDate(
                      project.lastmodifiedDate || project.updatedAt,
                      "MMM dd, yyyy"
                    )
                  : null
              }
              subValue={
                project.lastmodifiedDate || project.updatedAt
                  ? safeFormatDistance(
                      project.lastmodifiedDate || project.updatedAt,
                      { addSuffix: true }
                    )
                  : undefined
              }
            />
            <InfoRow
              icon={ShieldCheck}
              label="Approval"
              value={project.approvalStatus}
              valueClassName="capitalize"
            />
            <InfoRow icon={Hash} label="Project Code" value={project.code} valueClassName="font-mono" />
          </div>
        </SectionCard>

        {/* TAGS / EXTRAS */}
        <SectionCard title="Tags & Notes" icon={ClipboardList} delay={0.22} accent="slate">
          <div className="p-1.5 space-y-2">
            {project.tags && project.tags.length > 0 ? (
              <div>
                <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Tags
                </div>
                <div className="flex flex-wrap gap-1">
                  {project.tags.map((tag: string, index: number) => (
                    <span
                      key={index}
                      className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyHint text="No tags" />
            )}
            {project.notes && (
              <div className="p-2 bg-slate-50 rounded border border-slate-100">
                <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-0.5">
                  Notes
                </div>
                <p className="text-xs text-slate-700 leading-snug">{project.notes}</p>
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between text-[10px] text-slate-500 border-t border-slate-100 pt-3">
        <span>Manage assets, resources & checkpoints from quick actions</span>
        <button
          onClick={handleBack}
          className="text-teal-600 hover:text-teal-700 font-medium"
        >
          ← Back to projects
        </button>
      </div>
        </>
      )}

      {/* Equipment & Assets right-side drawer */}
      <RightDrawer
        open={openEquipmentDrawer}
        title={
          project
            ? `${projectName} - Equipment & Assets`
            : "Equipment & Assets"
        }
        onClose={() => setOpenEquipmentDrawer(false)}
        size="full"
      >
        <div className="p-0">
          {project && (
            <>
              <div className="border-b border-slate-200">
                <ProjectProgressHeader
                  project={project}
                  refreshSignal={progressRefreshKey}
                  onRefresh={() => {
                    loadProject();
                    setProgressRefreshKey((k) => k + 1);
                  }}
                />
              </div>
              <div className="p-6">
                <ProjectAssetManagement
                  project={project}
                  onEquipmentUpdate={() => {
                    loadProject();
                    setProgressRefreshKey((k) => k + 1);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </RightDrawer>
    </div>
  );
}
